import { useMutation, useQueryClient } from "@tanstack/react-query";

import { invalidateSkillCollectionDetailQueries } from "./cache-utils";

import { ENDPOINTS } from "@/api/application-api";
import { useAuth } from "@/context/auth-context";
import { ToastType } from "@/enums";
import { handleResponse } from "@/utils/errorResponseHandlers";
import { jsonAuthHeaders } from "@/utils/headers";
import { displayErrorToast, displayToast } from "@/utils/toast";

export interface FileUpdate {
  path: string;
  content: string;
}

export interface FileMove {
  oldPath: string;
  newPath: string;
}

interface SaveSkillCollectionVersionParams {
  workspaceId: number;
  collectionId: string;
  fileUpdates: FileUpdate[];
  moves?: FileMove[];
  deletes?: string[];
  commitMessage?: string;
  releaseLabel?: string;
  provider?: string | null;
}

interface SaveSkillCollectionVersionResponse {
  success: boolean;
  version: {
    id: string;
    number: number;
    created_at: string;
    commit_message?: string;
    release_label?: string;
  };
}

interface UseSaveSkillCollectionVersionOptions {
  onSuccess?: (
    data: SaveSkillCollectionVersionResponse,
    variables: SaveSkillCollectionVersionParams,
  ) => void;
}

export const useSaveSkillCollectionVersion = ({
  onSuccess,
}: UseSaveSkillCollectionVersionOptions) => {
  const auth = useAuth();
  const userToken = auth?.userToken ?? "";
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      workspaceId,
      collectionId,
      fileUpdates,
      moves,
      deletes,
      commitMessage,
      releaseLabel,
      provider,
    }: SaveSkillCollectionVersionParams): Promise<SaveSkillCollectionVersionResponse> => {
      const res = await fetch(ENDPOINTS.skill_collections.versions(workspaceId, collectionId), {
        method: "POST",
        headers: jsonAuthHeaders(userToken),
        body: JSON.stringify({
          file_updates: fileUpdates,
          moves: moves?.map(move => ({
            old_path: move.oldPath,
            new_path: move.newPath,
          })),
          deletes: deletes ?? [],
          commit_message: commitMessage,
          release_label: releaseLabel,
          provider,
        }),
      });
      return handleResponse(res);
    },
    onSuccess: (data, variables) => {
      invalidateSkillCollectionDetailQueries(queryClient, variables);
      displayToast("Version saved", ToastType.success);
      onSuccess?.(data, variables);
    },
    onError: (error: Error) => {
      displayErrorToast(error.message);
    },
  });
};
